"use client"

import { Cta, SanityCtaProps } from "@/components/cta"
import { RichText, RichTextValue } from "@/components/rich-text"
import { motion, useInView } from "motion/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { SlideOnView } from "@/components/slide-on-view"
import {
  SanityImageAsset,
  SanityImageHotspot,
  SanityReference,
} from "sanity-codegen"
import SanityImageField from "@/integrations/sanity/components/SanityImageField"
import { cn } from "@/utils/cn"
import { Cursor } from "@/components/cursor"
import { GridBg } from "@/svgs/grid-bg"
import { useAnimate } from "motion/react"
import { useIsMobile } from "@/ui/hooks/useIsMobile"
import { BadgeCheck } from "lucide-react"
import useEmblaCarousel from "embla-carousel-react"

interface TeamProps {
  content: {
    title: RichTextValue
    description: string
    cta: SanityCtaProps
    cards: {
      _key?: string
      image: {
        asset: SanityReference<SanityImageAsset>
        hotspot?: SanityImageHotspot
      }
      name: string
      verifiedExpert?: boolean
      skills: string[]
      description: string
      cta: SanityCtaProps
      footerText?: string
    }[]
  }
}

const MAX_STACK_CARDS = 4

const getHotspotObjectPosition = (hotspot?: SanityImageHotspot) => {
  if (typeof hotspot?.x !== "number" || typeof hotspot?.y !== "number") {
    return undefined
  }

  const x = Math.max(0, Math.min(1, hotspot.x)) * 100
  const y = Math.max(0, Math.min(1, hotspot.y)) * 100

  return `${x}% ${y}%`
}

export function Team({ content }: TeamProps) {
  const [showCursor, setShowCursor] = useState(false)
  const [cards, setCards] = useState(content.cards)
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    setCards(content.cards.toSorted(() => Math.random() - 0.5))
  }, [content.cards])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)
    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  const handleSlideUp = () => {
    setCards((currentCards) => {
      if (currentCards.length <= 1) return currentCards
      const [firstCard, ...restCards] = currentCards
      return [...restCards, firstCard]
    })
  }

  return (
    <div className="relative overflow-hidden bg-radial from-viola-100 to-neutral-light to-70% py-6 backdrop-blur-[370px] md:px-4 lg:py-20 xl:pt-50">
      <GridBg className="-top-1 -z-10" />
      <div className="mx-auto max-w-[90rem] px-6">
        <SlideOnView>
          <RichText value={content.title} />
        </SlideOnView>
        <SlideOnView
          delay={0.2}
          className="mt-6 font-family-gthaptik text-lg font-light xl:text-xlg"
        >
          {content.description}
        </SlideOnView>
        {content.cta?.label && (
          <SlideOnView
            delay={0.4}
            className="mt-8 flex justify-center xl:justify-start"
          >
            <Cta sanity={content.cta} />
          </SlideOnView>
        )}
      </div>
      <Cursor color="viola" label="SWIPE ME UP!" show={showCursor} />
      <div
        onMouseEnter={() => setShowCursor(true)}
        onMouseLeave={() => setShowCursor(false)}
        className="pb-[6rem] md:mt-40 md:pb-[6rem]"
      >
        <div ref={emblaRef} className="overflow-hidden md:hidden">
          <div className="flex items-center gap-4 py-5">
            {cards.map((card, index) => (
              <div
                key={card._key ?? index}
                className="flex min-w-0 flex-[0_0_90%] justify-center first:pl-6 last:pr-6"
              >
                <TeamCard
                  card={card}
                  isActive={index === selectedIndex}
                  preloadImage={index <= 1}
                />
              </div>
            ))}
          </div>
        </div>
        {cards.length > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2.5 md:hidden">
            {cards.map((card, index) => (
              <div
                key={card._key ?? index}
                role="button"
                tabIndex={0}
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => emblaApi?.scrollTo(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    emblaApi?.scrollTo(index)
                }}
                className={cn(
                  "h-2.5 shrink-0 cursor-pointer rounded-full transition-all duration-300",
                  index === selectedIndex
                    ? "w-7 bg-primary-500"
                    : "w-2.5 bg-viola-200"
                )}
              />
            ))}
          </div>
        )}
        <div className="relative hidden h-[38rem] w-full md:block md:h-[34rem]">
          {cards.map((card, index) => (
            <StackedTeamCard
              key={card._key ?? index}
              index={index}
              card={card}
              maxCards={MAX_STACK_CARDS}
              preloadImage={index <= MAX_STACK_CARDS}
              onSlide={handleSlideUp}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function TeamCard({
  card,
  isActive,
  preloadImage,
}: {
  card: TeamProps["content"]["cards"][number]
  isActive: boolean
  preloadImage: boolean
}) {
  const { cta, description, name, skills, image, footerText, verifiedExpert } =
    card
  const imageObjectPosition = getHotspotObjectPosition(image.hotspot)

  return (
    <motion.div
      className="mx-auto w-max min-w-0 shrink-0 grow-0 basis-1/1 rounded-lg bg-primary-500 pb-4 md:basis-3/5 md:pb-10 lg:basis-2/5"
      whileHover={{
        scale: 1.05,
        transition: { duration: 0.3 },
      }}
    >
      <div
        className={cn(
          "flex flex-col gap-3 rounded-lg border border-primary-500 bg-neutral-light p-4 md:flex-row md:gap-6"
        )}
      >
        <SanityImageField
          asset={image.asset}
          priority={preloadImage}
          objectPosition={imageObjectPosition}
          className="h-[16rem] w-full rounded-md bg-green-500 object-cover object-[0%_38%] md:h-[27rem] md:w-[20rem] md:object-center"
        />
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold">{name}</h3>
            {verifiedExpert && (
              <BadgeCheck className="size-5 text-primary-500" />
            )}
          </div>
          <div className="mt-2 flex w-full flex-wrap items-center gap-2.5">
            {skills.map((skill) => (
              <div
                key={skill}
                className="rounded-sm border border-viola-600 px-2.5 py-1.5 font-family-gtpressura text-viola-600"
              >
                {skill}
              </div>
            ))}
          </div>
          <p className="mt-4 max-w-[40rem] font-family-gthaptik font-light text-neutral-dark md:mt-6">
            {description}
          </p>
        </div>
      </div>
      {/* <div
        className={cn(
          "md:items-center, flex flex-col items-end px-6 py-4 md:flex-row",
          footerText ? "justify-between" : "justify-end"
        )}
      >
        {footerText && (
          <p
            className={cn(
              "font-family-gtpressura text-base",
              isActive ? "text-neutral-light" : "text-neutral-dark"
            )}
          >
            {footerText}
          </p>
        )}
        {cta?.label && <Cta sanity={cta} />}
      </div> */}
    </motion.div>
  )
}

function StackedTeamCard({
  index,
  card,
  maxCards,
  preloadImage,
  onSlide,
}: {
  index: number
  card: TeamProps["content"]["cards"][number]
  maxCards: number
  preloadImage: boolean
  onSlide: () => void
}) {
  const isMobile = useIsMobile()
  const scale = 1 - index * 0.1
  const isFirst = index === 0
  const yPosition = index * (isMobile ? -12 : -18)
  const zIndex = maxCards - index

  const [scope, animate] = useAnimate()

  const slideUp = () => {
    const nextScale = 1 - maxCards * 0.1
    const nextYPosition = maxCards * (isMobile ? -12 : -18)
    animate(
      scope.current,
      { y: `${nextYPosition}%`, scale: nextScale, zIndex: 0 },
      { duration: 0.3 }
    ).finished.then(() => {
      onSlide()
    })
  }

  return (
    <motion.div
      ref={scope}
      className={cn(
        "absolute inset-0 flex w-full items-start justify-center",
        index >= maxCards && "hidden"
      )}
      animate={{
        zIndex,
        scale,
        y: `${yPosition}%`,
      }}
      transition={{ duration: 0.3 }}
      drag="y"
      dragConstraints={{ top: -90, bottom: 0 }}
      dragElastic={0.2}
      onDragEnd={(_, info) => {
        if (isFirst && Math.abs(info.offset.y) > 40) slideUp()
      }}
    >
      <TeamCard card={card} isActive={isFirst} preloadImage={preloadImage} />
    </motion.div>
  )
}
